import React, { useState, useEffect } from 'react';
import { clickable } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const TEAM = [
  { name: 'Dimitri DE BACKER',  initials: 'DD', color: '#2563eb', email: 'dimitri.DE.BACKER@artemys-belgium.be' },
  { name: 'Mohamed ATAUIL',      initials: 'MA', color: '#7c3aed', email: 'Mohamed.ATAUIL@artemys-belgium.be' },
  { name: 'Aurélien BARBIEUX',   initials: 'AB', color: '#0891b2', email: 'Aurelien.BARBIEUX@artemys-belgium.be' },
  { name: 'Leslie BASHORUN',     initials: 'LB', color: '#d97706', email: 'Leslie.BASHORUN@artemys-belgium.be' },
  { name: 'Jonathan DEMORTIER',  initials: 'JD', color: '#dc2626', email: 'jonathan.demortier@artemys-belgium.be' },
  { name: 'Pierre BUIDIN',       initials: 'PB', color: '#9333ea', email: 'Pierre.Buidin@artemys-belgium.be' },
];

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7:00 - 19:00

function localDateStr(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function getWeekDays(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 5 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return day;
  });
}

function toLocalTime(dateStr) {
  try {
    // Parse as local time: strip trailing Z/offset so the browser treats it as local
    const s = dateStr.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    return new Date(s);
  } catch { return new Date(); }
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
}

function EventBlock({ event, color }) {
  const start = toLocalTime(event.start);
  const end   = toLocalTime(event.end);
  const startH = start.getHours() + start.getMinutes() / 60;
  const endH   = end.getHours()   + end.getMinutes() / 60;
  const top    = Math.max(0, (startH - 7) / 12 * 100);
  const height = Math.max(2, (endH - startH) / 12 * 100);
  const isPrivate = event.sensitivity === 'private' || event.sensitivity === 'confidential';
  const displaySubject = isPrivate ? 'Private' : event.subject;
  const timeStr = start.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) + ' - ' + end.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});
  const tooltipText = isPrivate ? ('Private\n' + timeStr) : (event.subject + '\n' + timeStr + (event.location ? '\n\u{1F4CD} '+event.location : ''));

  return (
    <div title={tooltipText}
      style={{
        position: 'absolute', left: '2px', right: '2px',
        top: `${top}%`, height: `${height}%`, minHeight: 18,
        background: isPrivate ? '#9ca3af88' : color + (event.showAs === 'tentative' ? '55' : 'cc'),
        border: `1px solid ${isPrivate ? '#9ca3af' : color}`,
        borderRadius: 4, padding: '1px 4px', overflow: 'hidden',
        cursor: 'pointer', zIndex: 1,
        borderLeft: `3px solid ${isPrivate ? '#9ca3af' : color}`,
      }}>
      <div style={{ fontSize: 9, fontWeight: 600, color: '#fff', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {isPrivate ? '🔒 Private' : displaySubject}
      </div>
      {height > 5 && (
        <div style={{ fontSize: 8, color: '#ffffff99' }}>
          {start.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}
        </div>
      )}
    </div>
  );
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendars, setCalendars]     = useState({});
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [emails, setEmails]           = useState({});
  const [showConfig, setShowConfig]   = useState(false);
  const [users, setUsers]             = useState([]);
  const [visibleMembers, setVisibleMembers] = useState(
    Object.fromEntries(TEAM.map(m => [m.name, true]))
  );

  const weekDays = getWeekDays(currentDate);
  const startStr = localDateStr(weekDays[0]);
  const endStr   = localDateStr(weekDays[4]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('calendarEmails') || '{}');
    // Merge defaults from TEAM array for any member not yet mapped
    const defaults = Object.fromEntries(TEAM.filter(m => m.email).map(m => [m.name, m.email]));
    const merged = { ...defaults, ...saved };
    // Persist so the UI reflects the defaults immediately
    localStorage.setItem('calendarEmails', JSON.stringify(merged));
    setEmails(merged);
  }, []);

  useEffect(() => {
    fetch(`${API}/api/calendar/users`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setUsers(d.users || []))
      .catch(() => {});
  }, []);

  const activeEmails = TEAM
    .filter(m => emails[m.name])
    .map(m => emails[m.name])
    .join(',');

  const load = () => {
    if (!activeEmails) return;
    setLoading(true); setError('');
    fetch(`${API}/api/calendar/events?start=${startStr}&end=${endStr}&emails=${encodeURIComponent(activeEmails)}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        const map = {};
        (d.calendars || []).forEach(cal => {
          const member = TEAM.find(m => emails[m.name] === cal.email);
          if (member) map[member.name] = cal.events || [];
        });
        setCalendars(map);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { if (activeEmails) load(); }, [startStr, endStr, activeEmails]);

  const saveEmail = (memberName, email) => {
    const updated = { ...emails, [memberName]: email };
    setEmails(updated);
    localStorage.setItem('calendarEmails', JSON.stringify(updated));
  };

  const today = new Date();
  const noEmailsConfigured = TEAM.every(m => !emails[m.name]);
  const activeTeam = TEAM.filter(m => emails[m.name] && visibleMembers[m.name]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
        <button onClick={() => setCurrentDate(d => { const n=new Date(d); n.setDate(n.getDate()-7); return n; })}
          style={{ padding:'5px 10px', border:'1px solid var(--border)', borderRadius:6, cursor:'pointer', background:'var(--surface-2)', fontSize:16 }}>‹</button>
        <button onClick={() => setCurrentDate(new Date())} className="btn btn-primary" style={{ padding:'5px 14px', fontSize:12 }}>Today</button>
        <button onClick={() => setCurrentDate(d => { const n=new Date(d); n.setDate(n.getDate()+7); return n; })}
          style={{ padding:'5px 10px', border:'1px solid var(--border)', borderRadius:6, cursor:'pointer', background:'var(--surface-2)', fontSize:16 }}>›</button>
        <span style={{ fontSize:13, fontWeight:600 }}>
          {weekDays[0].toLocaleDateString('en-GB',{day:'numeric',month:'long'})} – {weekDays[4].toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}
        </span>
        <div style={{ flex:1 }} />

        {/* Member toggles */}
        <div style={{ display:'flex', gap:6 }}>
          {TEAM.filter(m => emails[m.name]).map(m => (
            <div key={m.name} {...clickable(() => setVisibleMembers(v => ({...v,[m.name]:!v[m.name]})))}
              aria-pressed={!!visibleMembers[m.name]} aria-label={`Toggle ${m.name}`} title={m.name}
              style={{ width:28, height:28, borderRadius:'50%', background: visibleMembers[m.name] ? m.color : 'var(--border)',
                color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700,
                cursor:'pointer', transition:'all .15s', border:`2px solid ${visibleMembers[m.name] ? m.color : 'var(--border)'}` }}>
              {m.initials}
            </div>
          ))}
        </div>

        <button onClick={() => setShowConfig(c => !c)}
          style={{ padding:'5px 12px', border:'1px solid var(--border)', borderRadius:6, cursor:'pointer', background: showConfig ? 'var(--accent)' : 'var(--surface-2)', color: showConfig ? '#fff' : 'var(--text)', fontSize:12 }}>
          ⚙ Configure
        </button>
        <button onClick={load} disabled={loading || !activeEmails}
          style={{ padding:'5px 12px', border:'1px solid var(--border)', borderRadius:6, cursor:'pointer', background:'var(--surface-2)', fontSize:13 }}>
          {loading ? '⟳' : '↻'} Refresh
        </button>
      </div>

      {/* Config panel */}
      {showConfig && (
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'var(--surface-2)', flexShrink:0 }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Map team members to their Microsoft 365 email addresses:</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
            {TEAM.map(m => (
              <div key={m.name} style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:m.color, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, flexShrink:0 }}>{m.initials}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11, color:'var(--text-2)', marginBottom:2 }}>{m.name}</div>
                  {users.length > 0 ? (
                    <select value={emails[m.name] || ''} onChange={e => saveEmail(m.name, e.target.value)}
                      style={{ width:'100%', fontFamily:'DM Sans', fontSize:11, padding:'4px 6px', border:'1px solid var(--border)', borderRadius:5, background:'var(--surface)', outline:'none' }}>
                      <option value="">— not mapped —</option>
                      {users.map(u => <option key={u.email} value={u.email}>{u.name} ({u.email})</option>)}
                    </select>
                  ) : (
                    <input value={emails[m.name] || ''} onChange={e => saveEmail(m.name, e.target.value)}
                      placeholder="user@company.com"
                      style={{ width:'100%', fontFamily:'DM Sans', fontSize:11, padding:'4px 6px', border:'1px solid var(--border)', borderRadius:5, background:'var(--surface)', outline:'none', boxSizing:'border-box' }} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div style={{ padding:'8px 20px', background:'var(--critical-light)', color:'var(--critical)', fontSize:12, flexShrink:0 }}>{error}</div>}

      {noEmailsConfigured && !showConfig && (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, color:'var(--text-2)' }}>
          <div style={{ fontSize:48 }}>📅</div>
          <div style={{ fontSize:16, fontWeight:600 }}>Calendar not configured</div>
          <div style={{ fontSize:13 }}>Click ⚙ Configure above to map team members to their email addresses</div>
          <button onClick={() => setShowConfig(true)} className="btn btn-primary">Configure now</button>
        </div>
      )}

      {!noEmailsConfigured && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

          {/* ── Header: day names + all-day strip ── */}
          <div style={{ display:'flex', flexShrink:0, borderBottom:'2px solid var(--border)' }}>
            {/* Gutter label */}
            <div style={{ width:44, flexShrink:0, borderRight:'1px solid var(--border)', display:'flex', alignItems:'flex-end', justifyContent:'flex-end', paddingRight:6, paddingBottom:4 }}>
              <span style={{ fontSize:9, color:'var(--text-2)' }}>all day</span>
            </div>
            {weekDays.map((day, di) => {
              const isToday = isSameDay(day, today);
              const dayStr  = localDateStr(day);
              const allDayEvts = activeTeam.flatMap(m =>
                (calendars[m.name] || [])
                  .filter(ev => ev.isAllDay
                    && ev.start.split('T')[0] <= dayStr
                    && ev.end.split('T')[0]   >  dayStr)
                  .map(ev => ({ ...ev, member: m }))
              );
              return (
                <div key={di} style={{ flex:1, borderRight: di<4 ? '1px solid var(--border)' : 'none' }}>
                  {/* Day name */}
                  <div style={{ padding:'6px 0', textAlign:'center', background: isToday ? 'var(--accent-light)' : 'var(--surface-2)' }}>
                    <div style={{ fontSize:10, color:'var(--text-2)' }}>
                      {day.toLocaleDateString('en-GB',{weekday:'short'})}
                    </div>
                    <div style={{ fontSize:16, fontWeight:700, width:28, height:28, borderRadius:'50%',
                      display:'inline-flex', alignItems:'center', justifyContent:'center',
                      background: isToday ? 'var(--accent)' : 'transparent',
                      color: isToday ? '#fff' : 'var(--text)' }}>
                      {day.getDate()}
                    </div>
                  </div>
                  {/* All-day events */}
                  <div style={{ minHeight:20, padding:'2px 3px', display:'flex', flexDirection:'column', gap:1 }}>
                    {allDayEvts.map(ev => (
                      <div key={ev.id + ev.member.name}
                        title={(ev.sensitivity === 'private' || ev.sensitivity === 'confidential') ? ev.member.name + ': Private' : `${ev.member.name}: ${ev.subject}`}
                        style={{ fontSize:9, fontWeight:600, color:'#fff',
                          background: (ev.sensitivity === 'private' || ev.sensitivity === 'confidential') ? '#9ca3af88' : ev.member.color + 'cc',
                          borderLeft: `3px solid ${(ev.sensitivity === 'private' || ev.sensitivity === 'confidential') ? '#9ca3af' : ev.member.color}`,
                          borderRadius:3, padding:'1px 5px',
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {(ev.sensitivity === 'private' || ev.sensitivity === 'confidential') ? ev.member.initials + ' 🔒 Private' : ev.member.initials + ' ' + ev.subject}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Time grid ── */}
          <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
            {/* Hour gutter */}
            <div style={{ width:44, flexShrink:0, borderRight:'1px solid var(--border)' }}>
              {HOURS.map(h => (
                <div key={h} style={{ height:`${100/12}%`, paddingRight:6, display:'flex', alignItems:'flex-start', justifyContent:'flex-end' }}>
                  <span style={{ fontSize:10, color:'var(--text-2)', marginTop:-1 }}>{h}:00</span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
              {weekDays.map((day, di) => {
                const isToday = isSameDay(day, today);
                return (
                  <div key={di} style={{ flex:1, borderRight: di<4 ? '1px solid var(--border)' : 'none', position:'relative', overflowY:'auto' }}>
                    {/* Hour lines */}
                    {HOURS.map(h => (
                      <div key={h} style={{ position:'absolute', top:`${(h-7)/12*100}%`, left:0, right:0,
                        borderTop:`1px solid ${h===7?'transparent':'var(--border)'}`, opacity:.5 }} />
                    ))}
                    {/* Current time indicator */}
                    {isToday && (() => {
                      const now = new Date();
                      const pct = (now.getHours() + now.getMinutes()/60 - 7) / 12 * 100;
                      return pct >= 0 && pct <= 100 ? (
                        <div style={{ position:'absolute', top:`${pct}%`, left:0, right:0, height:2, background:'var(--critical)', zIndex:10, borderRadius:1 }}>
                          <div style={{ position:'absolute', left:-4, top:-3, width:8, height:8, borderRadius:'50%', background:'var(--critical)' }} />
                        </div>
                      ) : null;
                    })()}
                    {/* Timed events per member */}
                    {activeTeam.map((m, mi) => {
                      const memberEvents = (calendars[m.name] || []).filter(ev => {
                        if (ev.isAllDay) return false;
                        return isSameDay(toLocalTime(ev.start), day);
                      });
                      const width = 100 / activeTeam.length;
                      return memberEvents.map(ev => (
                        <div key={ev.id} style={{ position:'absolute', left:`${mi * width}%`, width:`${width}%`, top:0, bottom:0 }}>
                          <EventBlock event={ev} color={m.color} />
                        </div>
                      ));
                    })}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
